// src/pages/Orders.js
import React, { useState, useEffect, useMemo } from 'react';
import { listenToOrders, updateOrder } from '../services/firebase';
import toast from 'react-hot-toast';
import DateFilterBar from '../components/DateFilterBar';
import OrderDetailModal from '../components/OrderDetailModal';
import { searchOrders } from '../utils/orders';
import { formatOrderRef, formatOrderTitle } from '../utils/orderRef';
import { exportOrdersCsv } from '../utils/exportCsv';

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState('all');
  const [selectedDay, setSelectedDay] = useState('all');
  const [search, setSearch] = useState('');
  const [detailOrder, setDetailOrder] = useState(null);

  useEffect(() => { return listenToOrders(setOrders); }, []);

  const filtered = useMemo(() => {
    let list = filter === 'all' ? orders : orders.filter((o) => o.status === filter);
    if (selectedDay !== 'all') {
      list = list.filter((o) => {
        const t = o.createdAt?.toDate?.();
        if (!t) return false;
        return t.toLocaleDateString('en-CA') === selectedDay;
      });
    }
    return searchOrders(list, search);
  }, [orders, filter, selectedDay, search]);

  const statusColor = (s) => {
    if (s === 'delivered') return 'badge-green';
    if (s === 'pending') return 'badge-orange';
    if (s === 'cancelled' || s === 'failed') return 'badge-red';
    if (s === 'payment_pending') return 'badge-gray';
    if (s === 'confirmed') return 'badge-blue';
    return 'badge-gray';
  };

  const handleStatus = async (order, status, e) => {
    if (e) e.stopPropagation();
    const allowed = {
      pending: ['confirmed', 'cancelled'],
      payment_pending: ['cancelled', 'failed'],
      confirmed: ['cancelled', 'picked_up'],
      ready: ['confirmed', 'picked_up', 'cancelled'],
      picked_up: ['delivered', 'cancelled'],
    };
    const valid = allowed[order.status] || [];
    if (!valid.includes(status)) {
      toast.error(`Cannot change ${order.status} → ${status}`);
      return;
    }
    await updateOrder(order.id, { status });
    toast.success(`Order marked as ${status}`);
  };

  const handleExport = () => {
    const label = selectedDay === 'all' ? 'all' : selectedDay;
    exportOrdersCsv(filtered, `mytownly-orders-${label}.csv`);
    toast.success(`Exported ${filtered.length} orders`);
  };

  const fmt = (d) => d?.toDate ? d.toDate().toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

  return (
    <div className="page-body">
      <div style={{ marginBottom: 24, display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <div style={{ fontFamily: 'Clash Display, sans-serif', fontSize: 24, fontWeight: 700, marginBottom: 4 }}>Grocery Orders</div>
          <div style={{ fontSize: 13, color: 'var(--text2)' }}>
            {filtered.length} shown · {orders.length} loaded (last 500)
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <DateFilterBar selectedDay={selectedDay} onChange={setSelectedDay} showAll />
          <button type="button" className="btn btn-ghost btn-sm" onClick={handleExport}>Export CSV</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 320 }}>
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }}>🔍</span>
          <input
            placeholder="Search order #, phone, shop..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ paddingLeft: 32 }}
          />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {['all', 'pending', 'payment_pending', 'confirmed', 'picked_up', 'delivered', 'cancelled', 'failed'].map(f => (
          <button key={f} className={`btn ${filter === f ? 'btn-green' : 'btn-ghost'} btn-sm`} onClick={() => setFilter(f)} style={{ padding: '8px 12px', fontSize: 11, textTransform: 'capitalize', fontFamily: 'JetBrains Mono' }}>
            {f.replace('_', ' ')}
          </button>
        ))}
      </div>

      <div className="table-card">
        <table>
          <thead>
            <tr>
              <th>ORDER #</th>
              <th>SHOP</th>
              <th>ITEMS</th>
              <th>TOTAL</th>
              <th>ADDRESS</th>
              <th>PAYMENT</th>
              <th>TIME</th>
              <th>STATUS</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={9} style={{ textAlign: 'center', color: 'var(--text2)', padding: 32 }}>No orders found</td></tr>
            ) : filtered.map(o => (
              <tr key={o.id} onClick={() => setDetailOrder(o)} style={{ cursor: 'pointer' }}>
                <td><span style={{ fontFamily: 'JetBrains Mono', fontSize: 12, fontWeight: 700 }}>{formatOrderRef(o)}</span></td>
                <td style={{ fontSize: 12 }}>{o.shopName || '—'}</td>
                <td>
                  <div style={{ fontSize: 12, maxWidth: 160 }}>
                    {o.items?.slice(0, 2).map((item, i) => (
                      <span key={i}>{item.emoji || ''} {item.name}{i < Math.min(o.items.length, 2) - 1 ? ', ' : ''}</span>
                    ))}
                    {o.items?.length > 2 && <span style={{ color: 'var(--text2)' }}> +{o.items.length - 2} more</span>}
                  </div>
                </td>
                <td style={{ fontFamily: 'JetBrains Mono', color: 'var(--green)', fontWeight: 700 }}>₹{o.total || o.subtotal || '—'}</td>
                <td style={{ maxWidth: 140 }}><div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12, color: 'var(--text2)' }}>{o.deliveryAddress || '—'}</div></td>
                <td><span className={`badge ${o.paymentMode === 'cash' ? 'badge-gray' : 'badge-blue'}`}>{o.paymentMode || 'cash'}</span></td>
                <td style={{ fontFamily: 'JetBrains Mono', fontSize: 11, color: 'var(--text2)' }}>{fmt(o.createdAt)}</td>
                <td><span className={`badge ${statusColor(o.status)}`}>{o.status}</span></td>
                <td onClick={(e) => e.stopPropagation()}>
                  <select onChange={e => e.target.value && handleStatus(o, e.target.value, e)} defaultValue="" style={{ fontSize: 11, padding: '4px 8px', width: 120 }}>
                    <option value="">Update...</option>
                    {['pending', 'confirmed', 'picked_up', 'delivered', 'cancelled', 'failed'].map(s => (
                      <option key={s} value={s}>{s.replace('_', ' ')}</option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {detailOrder && <OrderDetailModal order={detailOrder} onClose={() => setDetailOrder(null)} />}
    </div>
  );
}
